import SteppedProgressBar from '@rio-cloud/rio-uikit/SteppedProgressBar';

import InvoicingDataStep from '@/components/steps/InvoicingStep/InvoicingDataStep';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import ShippingAddressStep, {
    type ShippingSchemaType,
} from '@/components/steps/ShippingAddressStep/ShippingAddressStep';
import SummaryStep from '@/components/steps/SummaryStep/SummaryStep';
import { t } from '@/lib/utils';

import ChargingCardsStep, { type ChargingCardsSchemaType } from '../../../components/steps/ChargingCardsStep';

interface StepContentProps {
    selectedStep: number;
    states: {
        chargingCardsState: ChargingCardsSchemaType;
        invoicingDataState: InvoiceDataSchemaType;
        shippingAddressState: ShippingSchemaType;
    };
    handlers: {
        handleChargingCardState: (state: ChargingCardsSchemaType) => void;
        handleInvoicingDataState: (state: InvoiceDataSchemaType) => void;
        handleShippingAddressState: (state: ShippingSchemaType) => void;
        handleReview: () => void;
        handleNext: () => void;
        handlePrevious: () => void;
        handleStepSelect: (step: number) => void;
    };
    isLoading: boolean;
    disableFollowingPages?: number;
    termsAndConditions?: {
        url?: string;
        englishUrl: string;
    };
}

const stepperIconHelper = (step: number, selectedStep: number) =>
    step < selectedStep ? (
        <i className='uikit-icon icon-ok' />
    ) : (
        <div className='padding-bottom-3 padding-right-1'>{step + 1}</div>
    );

export const StepContent: React.FC<StepContentProps> = ({
    selectedStep,
    states,
    handlers,
    isLoading,
    disableFollowingPages,
    termsAndConditions,
}) => {
    const pageContent = [
        <ChargingCardsStep
            key='chargingStep'
            state={states.chargingCardsState}
            setState={handlers.handleChargingCardState}
            handleNext={handlers.handleNext}
        />,
        <InvoicingDataStep
            key='invoiceDataStep'
            state={states.invoicingDataState}
            setState={handlers.handleInvoicingDataState}
            handleNext={handlers.handleNext}
            handleBack={handlers.handlePrevious}
        />,
        <ShippingAddressStep
            key='shippingAddress'
            state={states.shippingAddressState}
            setState={handlers.handleShippingAddressState}
            handleNext={handlers.handleNext}
            handleBack={handlers.handlePrevious}
        />,
        <SummaryStep
            chargingCardsState={states.chargingCardsState}
            invoicingDataState={states.invoicingDataState}
            shippingAddressState={states.shippingAddressState}
            key='summaryStep'
            setCallback={handlers.handleReview}
            onClickPrevious={handlers.handlePrevious}
            goToPage={handlers.handleStepSelect}
            isLoading={isLoading}
            isOrderNewCard={false}
            termsAndConditions={termsAndConditions}
        />,
    ];

    return (
        <>
            <div className='margin-bottom-25 padding-bottom-5'>
                <SteppedProgressBar
                    variant='circle'
                    selectedStepNumber={selectedStep}
                    onSelectedChanged={handlers.handleStepSelect}
                    disableFollowingPages={disableFollowingPages}
                    mobileBreakpoint={false}
                    labels={[
                        {
                            icon: stepperIconHelper(0, selectedStep),
                            label: t('general.chargingCards'),
                        },
                        {
                            icon: stepperIconHelper(1, selectedStep),
                            label: t('onboarding.stepper.invoicingAddress'),
                        },
                        {
                            icon: stepperIconHelper(2, selectedStep),
                            label: t('general.shippingAddress'),
                        },
                        {
                            icon: stepperIconHelper(3, selectedStep),
                            label: t('onboarding.stepper.summary'),
                        },
                    ]}
                />
            </div>
            {pageContent[selectedStep]}
        </>
    );
};
